import { useState } from "react";
import { Link } from "react-router-dom";
import useBlogPosts from "@/hooks/useBlogPosts";
import PasswordGate from "@/components/base/PasswordGate";

export default function BlogAdminPage() {
  const { posts, loaded, deletePost, resetToDefaults } = useBlogPosts();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const renderContent = () => {
    if (!loaded) {
      return (
        <main className="min-h-screen pt-24 md:pt-28 pb-16">
          <div className="flex items-center justify-center py-32">
            <div className="w-8 h-8 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin" />
          </div>
        </main>
      );
    }

    const handleDelete = (id: string) => {
      deletePost(id);
      setConfirmDelete(null);
    };

    const handleReset = () => {
      resetToDefaults();
      setConfirmReset(false);
    };

    const formatDate = (iso: string) =>
      new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });

    return (
      <main className="min-h-screen pt-24 md:pt-28 pb-16 md:pb-20">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-5xl mx-auto">
            <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
              <div>
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-1.5 font-body text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 mb-2"
                >
                  <i className="ri-arrow-left-line text-base" />
                  Back to Blog
                </Link>
                <h1 className="font-heading text-3xl md:text-4xl font-light text-foreground-950">
                  Manage Blog Posts
                </h1>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setConfirmReset(true)}
                  className="font-body text-xs text-foreground-500 hover:text-foreground-800 transition-colors duration-300 underline cursor-pointer whitespace-nowrap"
                >
                  Reset to defaults
                </button>
                <Link
                  to="/blog/admin/new"
                  className="inline-flex items-center gap-2 bg-primary-500 text-background-50 font-body text-sm font-medium px-5 py-2.5 rounded-md hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line text-lg" />
                  New Post
                </Link>
              </div>
            </header>

            {confirmReset && (
              <div className="mb-8 bg-accent-100 border border-accent-200 rounded-lg p-4 flex items-center justify-between">
                <p className="font-body text-sm text-accent-800">
                  Reset all posts to the default blog posts? This cannot be undone.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleReset}
                    className="bg-accent-500 text-background-50 font-body text-xs font-medium px-3 py-1.5 rounded-md hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Yes, reset
                  </button>
                  <button
                    onClick={() => setConfirmReset(false)}
                    className="text-foreground-600 font-body text-xs hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {posts.length === 0 ? (
              <div className="text-center py-20">
                <p className="font-body text-foreground-500 text-sm mb-4">No blog posts yet.</p>
                <Link
                  to="/blog/admin/new"
                  className="inline-block bg-primary-500 text-background-50 font-body text-sm font-medium px-5 py-2.5 rounded-md hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Create your first post
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 bg-background-50 rounded-lg p-4 border border-background-200/70"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-heading text-base font-medium text-foreground-950 truncate">
                          {post.title}
                        </h3>
                        {post.published ? (
                          <span className="flex-shrink-0 inline-block bg-primary-100 text-primary-700 font-label text-[10px] font-medium px-2 py-0.5 rounded-full">
                            Published
                          </span>
                        ) : (
                          <span className="flex-shrink-0 inline-block bg-foreground-100 text-foreground-500 font-label text-[10px] font-medium px-2 py-0.5 rounded-full">
                            Draft
                          </span>
                        )}
                      </div>
                      <p className="font-body text-xs text-foreground-500">
                        {post.category} &bull; {formatDate(post.updatedAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {post.published && (
                        <Link
                          to={`/blog/${post.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 font-body text-xs text-foreground-500 hover:text-foreground-800 px-3 py-1.5 rounded-md border border-background-200 hover:border-background-300 transition-all duration-200 whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-external-link-line text-sm" />
                          View
                        </Link>
                      )}
                      <Link
                        to={`/blog/admin/edit/${post.id}`}
                        className="inline-flex items-center gap-1 font-body text-xs text-foreground-700 hover:text-foreground-950 px-3 py-1.5 rounded-md border border-background-200 hover:border-background-300 transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-edit-line text-sm" />
                        Edit
                      </Link>
                      {confirmDelete === post.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="font-body text-[10px] text-accent-600 font-medium px-2 py-1 cursor-pointer whitespace-nowrap"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            className="font-body text-[10px] text-foreground-500 px-2 py-1 cursor-pointer whitespace-nowrap"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDelete(post.id)}
                          className="inline-flex items-center gap-1 font-body text-xs text-foreground-400 hover:text-accent-600 px-3 py-1.5 rounded-md border border-background-200 hover:border-accent-200 transition-all duration-200 whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    );
  };

  return (
    <PasswordGate returnPath="/blog">
      {renderContent()}
    </PasswordGate>
  );
}